// apps/office/src/components/office/ReceivePaymentDialog.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Wallet, Receipt } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const rs = (v) => {
  const n = Number(v) || 0;
  return `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n)}`;
};

const RECEIPT_METHODS = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card', 'Razorpay'];
const STATUS_OPTIONS = ['Completed', 'Pending', 'Failed', 'Refunded'];

const ReceivePaymentDialog = ({
  open,
  onOpenChange,
  quotation,          // { id, quoteNumber, total, clientName, clientJobId, expand }
  existingPayment,    // when editing
  client,             // { id, name, ... }
  dueAmount,          // suggested amount
  onSuccess,
}) => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!existingPayment;

  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [status, setStatus] = useState('Completed');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    if (existingPayment) {
      setAmount(String(existingPayment.amount || ''));
      setPaymentDate(existingPayment.paymentDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
      setPaymentMethod(existingPayment.paymentMethod || 'Cash');
      setStatus(existingPayment.status || 'Completed');
      setTransactionId(existingPayment.transactionId || existingPayment.reference || '');
      setNotes(existingPayment.notes || '');
    } else {
      setAmount(dueAmount > 0 ? String(Math.round(dueAmount)) : '');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('Cash');
      setStatus('Completed');
      setTransactionId('');
      setNotes('');
    }
  }, [open, existingPayment, dueAmount]);

  const savePayment = useMutation({
    mutationFn: async () => {
      const amt = parseFloat(amount) || 0;
      if (amt <= 0) throw new Error('Amount must be greater than 0');

      const payload = {
        amount: amt,
        paymentDate: paymentDate || new Date().toISOString().split('T')[0],
        paymentMethod,
        status,
        transactionId: transactionId || '',
        notes: notes || '',
        reference: transactionId || '',
      };

      if (!isEditing) {
        // Link to quotation/lead
        if (quotation?.id) {
          if (quotation.source === 'interior' || quotation.isInterior) {
            payload.source = 'interior';
            payload.interiorQuotationId = quotation.id;
          } else {
            payload.source = 'architecture';
            payload.quotationId = quotation.id;
          }
        }
        if (client?.id) payload.leadId = client.id;
        else if (quotation?.clientJobId) payload.leadId = quotation.clientJobId;
        if (currentUser?.id) payload.recordedBy = currentUser.id;
      }

      if (isEditing) {
        return pb.collection('payments').update(existingPayment.id, payload);
      }

      // Create the payment
      const record = await pb.collection('payments').create(payload);

      // ── Auto-convert the client if not already Converted ──
      const leadIdToConvert = client?.id || quotation?.clientJobId;
      if (leadIdToConvert) {
        try {
          const lead = await pb.collection('leads').getOne(leadIdToConvert);
          if (lead?.status && lead.status !== 'Converted') {
            const updateData = { status: 'Converted' };

            // TEMP job ID → Convert to HD
            if (lead.jobId && lead.jobId.startsWith('TEMP-')) {
              try {
                const { getNextConvertedJobId } = await import('../../lib/jobIdUtils');
                const newJobId = await getNextConvertedJobId();
                updateData.jobId = newJobId;
              } catch (e) {
                console.warn('[ReceivePayment] jobId generation failed:', e);
              }
            }

            await pb.collection('leads').update(leadIdToConvert, updateData);
            console.log('[ReceivePayment] Auto-converted client:', lead.name);
          }
        } catch (err) {
          // Non-critical — don't fail payment if lead conversion fails
          console.warn('[ReceivePayment] Auto-convert failed:', err);
        }
      }

      return record;
    },
    onSuccess: () => {
      toast.success(isEditing ? '✅ Payment updated' : '✅ Payment received & client converted');
      queryClient.invalidateQueries(['paymentsDashboard']);
      queryClient.invalidateQueries(['payments']);
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['lead']);
      queryClient.invalidateQueries(['interior-projects-all']);
      queryClient.invalidateQueries(['interior-clients-list']);
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err) => {
      let msg = err.message || 'Failed';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      toast.error('❌ ' + msg);
    },
  });

  const due = Number(dueAmount) || 0;
  const amtNum = parseFloat(amount) || 0;
  const newDue = Math.max(0, due - amtNum);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" />
            {isEditing ? 'Edit Payment' : 'Receive Payment'}
          </DialogTitle>
        </DialogHeader>

        {/* Context strip */}
        {(quotation || client) && (
          <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg text-xs">
            <div>
              <p className="text-slate-500 uppercase tracking-wide text-[10px]">Client</p>
              <p className="font-semibold text-slate-800 truncate">
                {client?.name || quotation?.clientName || '—'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 uppercase tracking-wide text-[10px]">Quotation</p>
              <p className="font-mono font-semibold text-indigo-600">
                {quotation?.quoteNumber || '—'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 uppercase tracking-wide text-[10px]">Due</p>
              <p className="font-bold text-red-600">{rs(due)}</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-emerald-700 font-medium">Amount Received (Rs.) *</Label>
            <Input
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`e.g., ${due > 0 ? Math.round(due) : 5000}`}
              className="border-emerald-200 text-lg font-bold"
              autoFocus
            />
            {!isEditing && due > 0 && (
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setAmount(String(Math.round(due)))}
                  className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                >
                  Full due ({rs(due)})
                </button>
                <button
                  type="button"
                  onClick={() => setAmount(String(Math.round(due / 2)))}
                  className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                >
                  50%
                </button>
              </div>
            )}
            {!isEditing && amtNum > 0 && due > 0 && (
              <p className="text-xs text-slate-500 mt-1.5">
                After this payment, remaining due will be <strong>{rs(newDue)}</strong>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-emerald-700 font-medium">Payment Date *</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="border-emerald-200"
              />
            </div>
            <div>
              <Label className="text-emerald-700 font-medium">Method *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="border-emerald-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECEIPT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-emerald-700 font-medium">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="border-emerald-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-emerald-700 font-medium">Transaction / Ref.</Label>
              <Input
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="UPI ID, Cheque no., etc."
                className="border-emerald-200"
              />
            </div>
          </div>

          <div>
            <Label className="text-emerald-700 font-medium">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details…"
              className="border-emerald-200"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={savePayment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => savePayment.mutate()}
            disabled={savePayment.isPending || !amount || parseFloat(amount) <= 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          >
            {savePayment.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            {isEditing ? 'Save Changes' : 'Save Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReceivePaymentDialog;
